import { AnimationDriver } from '@angular/animations/browser';
import { APP_BASE_HREF } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { APP_BOOTSTRAP_LISTENER, ErrorHandler, NgModule, NgZone } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

import { animationPlayerFactory } from './animation-player.factory';
import { markAppBootstrapCompleted } from './app';
import { AppRoutingModule } from './app-routing.module';
import { OfficeHomeAppShellModule } from './app-shell/officehome-app-shell.module';
import { AppTilesModule } from './app-tiles/app-tiles.module';
import { AppComponent } from './app.component';
import { BottomTrayModule } from './bottom-tray/bottom-tray.module';
import { OfficeHomePageErrorHandler } from './error-handler/error-handler.service';
import { HeroModule } from './hero/hero.module';
import { HomeModule } from './home/home.module';
import { appOrderedHttpInterceptors } from './http-interceptor-ordering/app.interceptor-ordering';
import { LazyViewModule } from './lazy-view/lazy-view.module';
import { AppRouteReuseStrategy } from './route-reuse-strategies/app.route-reuse-strategy';
import { DiskPreloadModule } from './service-worker/disk-preload/disk-preload.module';
import { DecryptResponseService } from './service-worker/encryption/decrypt-response.service';
import { OfficeDecryptResponseService } from './service-worker/encryption/office-decrypt-response.service';
import { ServicedWorkerEncryptionModule } from './service-worker/encryption/service-worker-encryption.module';
import { ServiceWorkerActivationTelemetryHandlerService } from './service-worker/registration-handlers/service-worker-activation-telemetry-handler.service';
import { SERVICE_WORKER_REGISTRATION_HANDLERS } from './service-worker/registration-handlers/service-worker-registration-handlers.injection-token';
import { UnregisterIfDisabledService } from './service-worker/registration-handlers/unregister-if-disabled.service';
import { ServiceWorkerModule } from './service-worker/service-worker.module';
import { shellHeight } from './shared/constants/shell-height.constant';
import { ShellHeightToken } from './shared/constants/shell-height.token';
import { WorkloadQueryParameter } from './shared/constants/workload-query-parameter.enum';
import { WorkloadQueryParameterToken } from './shared/constants/workload-query-parameter.injection-token';
import { SharedComponentsModule } from './shared/directives/shared-components.module';
import { IconFontModule } from './shared/icon-font/icon-font.module';
import { instrumentationQueueFactory } from './shared/instrumentation/instrumentation-queue.factory';
import { INSTRUMENTATION_QUEUE } from './shared/instrumentation/instrumentation-queue.injection-token';
import { InstrumentationModule } from './shared/instrumentation/instrumentation.module';
import { ResizeModule } from './shared/resize/resize.module';
import { BaseUpdateContentService } from './shared/services/base-update-content.service';
import { BasicServicesModule } from './shared/services/basic-services.module';
import { DataSecurityService } from './shared/services/data-security.service';
import { DeviceDetector } from './shared/services/device-detector.service';
import { idleCallbackServiceFactory } from './shared/services/idle-callback-service.factory';
import { IdleCallbackService } from './shared/services/idle-callback.service';
import { OfficeHomeServicesModule } from './shared/services/office-home-services.module';
import { performanceQueueFactory } from './shared/services/performance-queue.factory';
import { PERFORMANCE_QUEUE } from './shared/services/performance-queue.injection-token';
import { UpdateContentService } from './shared/services/update-content.service';
import { WebDataSecurityService } from './shared/services/web-data-security.service';
import { ClientStartupConfigService } from './shared/shared-context/client-startup-config.service';

@NgModule({
    imports: [
        AppRoutingModule,
        AppTilesModule,
        BasicServicesModule,
        BrowserAnimationsModule,
        BrowserModule,
        BottomTrayModule,
        DiskPreloadModule,
        HeroModule,
        HomeModule,
        HttpClientModule,
        IconFontModule,
        InstrumentationModule.forRoot(),
        LazyViewModule,
        RouterModule,
        OfficeHomeServicesModule,
        ServiceWorkerModule,
        ServicedWorkerEncryptionModule,
        ResizeModule,
        SharedComponentsModule.forRoot(),
        OfficeHomeAppShellModule
    ],
    providers: [
        CookieService,
        { provide: ShellHeightToken, useValue: shellHeight },
        { provide: ErrorHandler, useClass: OfficeHomePageErrorHandler },
        { provide: DataSecurityService, useClass: WebDataSecurityService },
        { provide: AnimationDriver, useFactory: animationPlayerFactory, deps: [ClientStartupConfigService] },
        { provide: APP_BASE_HREF, useValue: '/' }, // This is needed in case <head> does not have <base href='/'> element: https://stackoverflow.com/questions/34535163/angular-2-router-no-base-href-set
        { provide: APP_BOOTSTRAP_LISTENER, useFactory: markAppBootstrapCompleted, multi: true },
        { provide: WorkloadQueryParameterToken, useValue: WorkloadQueryParameter.OfficeHome },
        { provide: SERVICE_WORKER_REGISTRATION_HANDLERS, useClass: UnregisterIfDisabledService, multi: true },
        { provide: SERVICE_WORKER_REGISTRATION_HANDLERS, useClass: ServiceWorkerActivationTelemetryHandlerService, multi: true },
        { provide: INSTRUMENTATION_QUEUE, useFactory: instrumentationQueueFactory },
        { provide: PERFORMANCE_QUEUE, useFactory: performanceQueueFactory },
        { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
        { provide: IdleCallbackService, useFactory: idleCallbackServiceFactory, deps: [ClientStartupConfigService, DeviceDetector, NgZone] },
        { provide: UpdateContentService, useClass: BaseUpdateContentService },
        { provide: DecryptResponseService, useClass: OfficeDecryptResponseService },
        appOrderedHttpInterceptors
    ],
    declarations: [
        AppComponent
    ],
    bootstrap: [
        AppComponent
    ]
})
export class AppModule {
}
